import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'primereact/dropdown';
import { cn } from '@/lib/utils';

export interface ComboboxOption {
  value: string;
  label: string;
  sublabel?: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  allowClear?: boolean;
  onFilterChange?: (value: string) => void;
  /** Options are already filtered by the server (typed text goes to onFilterChange): skip the client-side filter. */
  serverSearch?: boolean;
  /** Server search in flight (or debounce pending): show the loading row, never the empty message. */
  loading?: boolean;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyText = 'No results found.',
  disabled = false,
  className,
  allowClear = false,
  onFilterChange,
  serverSearch = false,
  loading = false,
}: ComboboxProps) {
  const { t } = useTranslation('common');
  const emptyMessage = loading ? t('searching') : emptyText;
  const [typed, setTyped] = useState('');
  // Server search: every option carries the typed text so PrimeReact's own filter keeps all of them.
  const primeOptions = options.map((o) => ({ label: o.label, value: o.value, sublabel: o.sublabel, filterKey: typed }));

  const itemTemplate = (option: ComboboxOption) => (
    <div className="flex flex-col">
      <span className="text-sm">{option.label}</span>
      {option.sublabel && (
        <span className="text-xs text-muted-foreground">{option.sublabel}</span>
      )}
    </div>
  );

  return (
    <Dropdown
      options={primeOptions}
      value={value || null}
      onChange={(e) => onChange(e.value ?? '')}
      placeholder={placeholder}
      filter
      filterPlaceholder={searchPlaceholder}
      filterBy={serverSearch ? 'filterKey' : undefined}
      onFilter={onFilterChange || serverSearch ? (e) => { setTyped(e.filter); onFilterChange?.(e.filter); } : undefined}
      emptyMessage={emptyMessage}
      emptyFilterMessage={emptyMessage}
      disabled={disabled}
      showClear={allowClear}
      itemTemplate={itemTemplate}
      className={cn('w-full', className)}
      pt={{ input: { className: 'text-sm' } }}
    />
  );
}

